import some from 'lodash/some';
import values from 'lodash/values';

import {
  type SetMyStationsDisplayAction,
  type SetMyStationsListSortAction,
  type UpdateMyStationsListVisibleFieldsAction,
  type ValueOf,
} from 'src/types';

import {
  MyStationDisplayTypes,
  MyStationFilterTypes,
  MyStationsActions,
  MyStationsKeys,
  SORT_BY,
} from '../constants/MyStations';
import { Map } from '../lib/ImmutableUtils';
import { getMyStationsListFields, getMyStationsListSort } from '../lib/MyStationsUtils';
import { createReducer } from '../lib/ReducerUtils';

const INITIAL_STATE = Map({
  [MyStationsKeys.DISPLAY_TYPE]: MyStationDisplayTypes.RECENT as ValueOf<
    typeof MyStationDisplayTypes
  >,
  [MyStationsKeys.LIST_CONFIG]: Map({
    [MyStationsKeys.IS_DESC]: false,
    [MyStationsKeys.IS_INITIAL_SORT]: true,
    [MyStationsKeys.LIST_SORT]: SORT_BY.alphabetic as ValueOf<typeof SORT_BY>,
    [MyStationsKeys.FIELD_VISIBILITY]: Map({
      [SORT_BY.alphabetic]: true,
      [SORT_BY.playTime]: true,
      [SORT_BY.createdTime]: true,
      [SORT_BY.recency]: false,
    }),
  }),
});
export type MyStationsState = typeof INITIAL_STATE;

// MY STATIONS REDUCER
export default createReducer(INITIAL_STATE, {
  [MyStationsActions.SET_MY_STATIONS_DISPLAY]: setMyStationsDisplay,
  [MyStationsActions.SET_MY_STATIONS_DISPLAY_SETTINGS]: setMyStationsDisplaySettings,
  [MyStationsActions.SET_MY_STATIONS_LIST_SORT]: setMyStationsListSort,
  [MyStationsActions.UPDATE_MY_STATIONS_LIST_VISIBLE_FIELDS]: updateMyStationsListVisibleFields,
});

// CASE FUNCTIONS

function setMyStationsDisplay(
  state: MyStationsState,
  { payload: { newDisplayType } }: SetMyStationsDisplayAction,
): MyStationsState {
  if (!some(values(MyStationFilterTypes), val => val === newDisplayType)) {
    return state; // invalid display type
  }
  return state.set(MyStationsKeys.DISPLAY_TYPE, newDisplayType);
}

function setMyStationsListSort(
  state: MyStationsState,
  { payload: { isDesc, sortType } }: SetMyStationsListSortAction,
): MyStationsState {
  if (!some(values(SORT_BY), val => val === sortType)) {
    return state; // invalid sort type
  }
  return state.mergeDeep({
    [MyStationsKeys.LIST_CONFIG]: {
      [MyStationsKeys.LIST_SORT]: sortType,
      [MyStationsKeys.IS_DESC]: !!isDesc,
      [MyStationsKeys.IS_INITIAL_SORT]: false,
    },
  });
}

function setMyStationsDisplaySettings(state: MyStationsState, action: $TSFixMe): MyStationsState {
  const newState = setMyStationsDisplay(state, action);
  return setMyStationsListSort(newState, action);
}

function updateMyStationsListVisibleFields(
  state: MyStationsState,
  { payload: { fieldToUpdate, newValue } }: UpdateMyStationsListVisibleFieldsAction,
): MyStationsState {
  if (!getMyStationsListFields(state).has(fieldToUpdate)) {
    return state; // invalid field
  }
  const updatedFields = getMyStationsListFields(state).set(fieldToUpdate, newValue);
  if (newValue === false && fieldToUpdate === getMyStationsListSort(state)) {
    // we are removing the current sort, reset to alphabetic
    const updatedListConfig = INITIAL_STATE.get(MyStationsKeys.LIST_CONFIG).set(
      MyStationsKeys.FIELD_VISIBILITY,
      updatedFields,
    );
    return state.set(MyStationsKeys.LIST_CONFIG, updatedListConfig);
  }
  return state.setIn([MyStationsKeys.LIST_CONFIG, MyStationsKeys.FIELD_VISIBILITY], updatedFields);
}
